import {type AppBskyActorDefs} from '@atproto/api'

import {
  type Classroom,
  type ClassroomApprovers,
  type ClassroomContent,
  type ClassroomLevelMove,
  type ClassroomMember,
  type JoinRequest,
  type MemberRole,
} from '#/state/classrooms/types'

type ProfileJson = Pick<
  AppBskyActorDefs.ProfileViewBasic,
  'did' | 'handle' | 'displayName' | 'avatar'
>

export type ApproversJson = {mode: 'all-teachers' | 'list'; dids?: string[]}

export type ViewerStateJson = {
  role?: MemberRole
  isModerator?: boolean
  canInvite?: boolean
  canPost?: boolean
  canManage?: boolean
  request?: {createdAt: string; declinedAt?: string}
}

export type ClassroomViewJson = {
  id: string
  owner: ProfileJson
  handle: string
  name: string
  description: string
  coverUrl?: string
  visibility: 'public' | 'private'
  level: string
  grade?: string
  levelLabel?: string
  levelNameType: string
  country?: string
  international: boolean
  parentsCanJoin: boolean
  autoAcceptWithCode: boolean
  joinCode?: string
  studentApprovers?: ApproversJson
  parentApprovers?: ApproversJson
  interests?: string[]
  levelChangedAt?: string
  levelMoves?: ClassroomLevelMove[]
  counts: {teachers: number; students: number; parents: number}
  contentCount: number
  createdAt: string
  viewer: ViewerStateJson
}

export type MemberViewJson = {
  profile: ProfileJson
  role: MemberRole
  isModerator: boolean
  forStudentDid?: string
  joinedAt: string
}

export type RequestViewJson = {
  profile: ProfileJson
  role: string
  message?: string
  createdAt: string
}

export type ContentViewJson = {
  id: string
  classroomId: string
  kind: 'post' | 'capsule' | 'flashbook' | 'module'
  visibility: 'public' | 'members'
  audience?: string[]
  author: ProfileJson
  postUri?: string
  postCid?: string
  capsuleUri?: string
  capsuleCid?: string
  flashbookUri?: string
  flashbookCid?: string
  moduleUri?: string
  moduleCid?: string
  title?: string
  text: string
  announcement?: string
  announcedLevel?: string
  announcedGrade?: string
  classroomLevel?: string
  classroomGrade?: string
  createdAt: string
  acknowledged: string[]
  viewerCanManage?: boolean
}

export type ClassroomViewer = {
  role: MemberRole | null
  isModerator: boolean
  canInvite: boolean
  canPost: boolean
  canManage: boolean
  request: {createdAt: string; declinedAt?: string} | null
}

function fromApprovers(a?: ApproversJson): ClassroomApprovers | undefined {
  if (!a) return undefined
  return a.mode === 'list' ? (a.dids ?? []) : 'all-teachers'
}

export function toApproversJson(value: ClassroomApprovers): ApproversJson {
  return value === 'all-teachers'
    ? {mode: 'all-teachers'}
    : {mode: 'list', dids: value}
}

export function toClassroom(view: ClassroomViewJson): Classroom {
  return {
    id: view.id,
    handle: view.handle,
    name: view.name,
    description: view.description,
    coverUrl: view.coverUrl,
    visibility: view.visibility,
    level: view.level,
    grade: view.grade,
    levelLabel: view.levelLabel,
    country: view.country,
    international: view.international,
    levelChangedAt: view.levelChangedAt,
    // Anything the server sends that we do not know is treated as standard
    levelNameType: view.levelNameType === 'SYSTEM' ? 'SYSTEM' : 'STANDARD',
    parentsCanJoin: view.parentsCanJoin,
    autoAcceptWithCode: view.autoAcceptWithCode,
    studentApprovers: fromApprovers(view.studentApprovers),
    parentApprovers: fromApprovers(view.parentApprovers),
    interests: view.interests ?? [],
    ownerDid: view.owner.did,
    ownerHandle: view.owner.handle,
    ownerName: view.owner.displayName,
    joinCode: view.joinCode,
    levelMoves: view.levelMoves,
    publishedCounts: view.counts,
    contentCount: view.contentCount,
    members: [],
    requests: [],
    createdAt: view.createdAt,
  }
}

export function toMember(view: MemberViewJson): ClassroomMember {
  return {
    did: view.profile.did,
    handle: view.profile.handle,
    displayName: view.profile.displayName,
    avatar: view.profile.avatar,
    role: view.role,
    joinedAt: view.joinedAt,
    forStudentDid: view.forStudentDid,
    isModerator: view.isModerator,
  }
}

export function moderatorDids(members: ClassroomMember[]): string[] {
  return members.filter(m => m.isModerator).map(m => m.did)
}

export function toRequest(view: RequestViewJson): JoinRequest {
  return {
    did: view.profile.did,
    handle: view.profile.handle,
    displayName: view.profile.displayName,
    avatar: view.profile.avatar,
    role: view.role as JoinRequest['role'],
    requestedAt: view.createdAt,
    message: view.message,
  }
}

export function toContent(view: ContentViewJson): ClassroomContent {
  return {
    id: view.id,
    classroomId: view.classroomId,
    kind: view.kind,
    visibility: view.visibility,
    audience: view.audience as ClassroomContent['audience'],
    text: view.text,
    capsuleTitle: view.kind === 'capsule' ? view.title : undefined,
    flashbookTitle: view.kind === 'flashbook' ? view.title : undefined,
    moduleTitle: view.kind === 'module' ? view.title : undefined,
    authorDid: view.author.did,
    authorHandle: view.author.handle,
    authorName: view.author.displayName,
    authorAvatar: view.author.avatar,
    postUri: view.postUri,
    postCid: view.postCid,
    capsuleUri: view.capsuleUri,
    capsuleCid: view.capsuleCid,
    flashbookUri: view.flashbookUri,
    flashbookCid: view.flashbookCid,
    moduleUri: view.moduleUri,
    moduleCid: view.moduleCid,
    announcement: view.announcement === 'level' ? 'level' : undefined,
    announcedLevel: view.announcedLevel,
    announcedGrade: view.announcedGrade,
    classroomLevel: view.classroomLevel,
    classroomGrade: view.classroomGrade,
    acknowledged: view.acknowledged,
    viewerCanManage: view.viewerCanManage,
    createdAt: view.createdAt,
  }
}

export function toViewer(view: ClassroomViewJson): ClassroomViewer {
  const v = view.viewer
  return {
    role: v.role ?? null,
    isModerator: v.isModerator ?? false,
    canInvite: v.canInvite ?? false,
    canPost: v.canPost ?? false,
    canManage: v.canManage ?? false,
    request: v.request ?? null,
  }
}
